import { type Comment, type CommentSortOrder, CommentStatus, type Post } from '@src/store/contentEntities/model';
import { orderBy, type QueryConstraint, type QueryDocumentSnapshot, where } from 'firebase/firestore/lite';
import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { atomWithImmer } from 'jotai-immer';
import { createCollectionAtomFamily, createLoadMoreAtom, fetchEntities, fetchEntity } from '../common/collectionAtom';
import { jotaiStore } from '../store';

export type CommentCollectionType = 'post' | 'user' | 'pending' | 'comment'; // 'comment' is for replies

export const commentsAtom = atomWithImmer<Record<string, Comment>>({});
commentsAtom.debugLabel = 'commentsAtom';

export function setComment(comment: Comment) {
    if (!comment || !comment.id) return; // Early return if comment or id is invalid
    jotaiStore.set(commentsAtom, draft => {
        draft[comment.id] = comment;
    });
}

export function setComments(comments: Record<string, Comment>) {
    if (!comments || Object.keys(comments).length === 0) return; // Early return if comments is empty
    jotaiStore.set(commentsAtom, draft => {
        Object.assign(draft, comments);
    });
}

export const getCommentByIdAtom = atom(get => (commentId: string | undefined): Comment | undefined => {
    if (!commentId) return;
    const comments = get(commentsAtom);
    return comments[commentId];
});
getCommentByIdAtom.debugLabel = 'getCommentByIdAtom';

// --- Derived Atoms for Reading Comment Data ---
export const commentAtomFamily = atomFamily((commentId: string | undefined) => {
    const derivedAtom = atom<Comment | undefined>(get => (commentId ? get(commentsAtom)[commentId] : undefined));
    derivedAtom.debugLabel = `commentAtomFamily/${commentId ?? 'undefined'}`;
    return derivedAtom;
});

// ----- Comment Collection State Management -----
export interface CommentCollectionCompositeKeyParams {
    type: CommentCollectionType;
    postId?: string;
    userId?: string;
    tokenName?: string;
    parentCommentId?: string;
    includeDeleted?: boolean;
    fetchLimit?: number;
    sortOrder?: CommentSortOrder;
}

export function generateCommentCollectionKey(params: CommentCollectionCompositeKeyParams): string {
    let key = `${params.type}`;
    if (params.postId) key += `_post-${params.postId}`;
    if (params.userId) key += `_user-${params.userId}`;
    if (params.tokenName) key += `_token-${params.tokenName}`;
    if (params.parentCommentId) key += `_parent-${params.parentCommentId}`;
    if (params.includeDeleted !== undefined) key += `_deleted-${params.includeDeleted}`;
    if (params.sortOrder) key += `_sort-${params.sortOrder}`;
    return key;
}

export const commentCollectionAtomFamily = createCollectionAtomFamily<CommentCollectionCompositeKeyParams, Comment>({
    generateKey: generateCommentCollectionKey,
    equalityCheck: (a, b) => generateCommentCollectionKey(a) === generateCommentCollectionKey(b),
    debugLabel: 'commentCollectionState',
});

// --- Query Constraints Generation ---
const getQueryConstraints = (params: CommentCollectionCompositeKeyParams): QueryConstraint[] => {
    const { type, postId, userId, tokenName, parentCommentId, includeDeleted, sortOrder } = params;
    const constraints: QueryConstraint[] = [];

    const visibleStatuses = [
        CommentStatus.PUBLISHED,
        CommentStatus.DELETED_BY_TOKEN_ADMIN,
        CommentStatus.DELETED_BY_PUBLISHER,
        CommentStatus.DELETED_BY_DOMAIN_ADMIN,
        CommentStatus.DELETED_BY_CATEGORY_ADMIN, // Include deprecated for filtering existing data
    ];

    switch (type) {
        case 'post': {
            if (!postId) throw new Error('postId is required for post comment collection');
            constraints.push(
                where('postId', '==', postId),
                where('status', 'in', visibleStatuses),
                where('replyToCommentId', '==', '') // Top-level comments only
            );

            // Apply sort order for post comments
            const effectiveSortOrder = sortOrder || 'best';
            if (effectiveSortOrder === 'best') {
                constraints.push(orderBy('reactionsCount.thumb-up', 'desc'));
            } else if (effectiveSortOrder === 'newest') {
                constraints.push(orderBy('createdAt', 'desc'));
            } else if (effectiveSortOrder === 'oldest') {
                constraints.push(orderBy('createdAt', 'asc'));
            }
            break;
        }

        case 'user': {
            if (!userId || !tokenName) throw new Error('userId and tokenName are required for user comment collection');
            const userVisibleStatuses = includeDeleted ? visibleStatuses : [CommentStatus.PUBLISHED];
            constraints.push(
                where('publisherId', '==', userId),
                where('tokenName', '==', tokenName),
                where('status', 'in', userVisibleStatuses),
                orderBy('createdAt', 'desc') // Default order for user comments
            );
            break;
        }

        case 'pending':
            if (!tokenName) throw new Error('tokenName is required for pending comment collection');
            constraints.push(where('tokenName', '==', tokenName), where('status', '==', CommentStatus.PENDING), orderBy('createdAt', 'asc'));
            break;

        case 'comment': // Replies to a specific comment
            if (!parentCommentId) throw new Error('parentCommentId is required for comment replies collection');
            constraints.push(where('replyToCommentId', '==', parentCommentId), where('status', 'in', visibleStatuses), orderBy('createdAt', 'asc'));
            break;

        default:
            ((_: never) => {
                throw new Error(`Unsupported comment type: ${type}`);
            })(type);
    }

    return constraints;
};

export type LoadMoreCommentsParams = CommentCollectionCompositeKeyParams & { fetchLimit?: number };
export const loadMoreCommentsAtom = createLoadMoreAtom<Comment, CommentCollectionCompositeKeyParams>(commentCollectionAtomFamily, 'Comment', getQueryConstraints, commentsAtom);

// Atom to load a single comment if not already present
export const loadCommentByIdAtom = atom(null, async (get, set, commentId: string) => {
    const existingComment = get(getCommentByIdAtom)(commentId);
    if (existingComment) return existingComment;

    const comment = await fetchEntity('Comment', commentId);
    if (!comment) throw new Error(`Comment with ID ${commentId} not found`);

    setComment(comment as Comment); // Cast to Comment
    return comment as Comment; // Cast to Comment
});
loadCommentByIdAtom.debugLabel = 'loadCommentByIdAtom';

export function getCommentsAtom() {
    return commentsAtom;
}

export function getComments() {
    return jotaiStore.get(commentsAtom);
}

export function getComment(commentId: string): Comment | undefined {
    return getComments()[commentId];
}
